import { AxiosError } from "axios";
import React from "react";
import { Theme } from "../../theme";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  Text,
  IconButton,
  PinInput,
  PinInputField,
  useTheme,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import getAxios from "../../hooks/useAxios";
import Cookies from "js-cookie";
import { encrypted } from "../../utils/crypto";

const Otp = () => {
  const axios = getAxios();
  const theme = useTheme<Theme>();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const success = params.get("success");
  const token = params.get("token");
  const phone = params.get("phone");
  const fromPasswordChange = params.get("fromPasswordChange");

  const timerValue = 180;
  const pinLength = 6;
  const [otp, setOtp] = React.useState<string>();
  const [otpErrorMessage, setOtpErrorMessage] = React.useState<boolean>(false);
  const [otpSendSuccessMessage, setOtpSendSuccessMessage] =
    React.useState<boolean>(success === "true" ? true : false);
  const [isInvalidPin, setIsInvalidPin] = React.useState<boolean>();
  const [toggleOtpTimer, setToggleOtpTimer] = React.useState(true);
  const [verifyInProgress, setVerifyInProgress] = React.useState(false);
  const [minutes, setMinutes] = React.useState<string>();
  const [seconds, setSeconds] = React.useState<string>();
  const [timer, setTimer] = React.useState(timerValue);

  const handleBackButton = () => {
    navigate("/");
  };

  const handleInputChange = (val: string) => {
    setOtp(val);
  };
  React.useEffect(() => {
    setIsInvalidPin(otpErrorMessage);
  }, [otpErrorMessage]);

  React.useEffect(() => {
    const minuteFromTimer = Math.floor(timer / 60).toLocaleString();
    setMinutes(minuteFromTimer);
    const secondFromTimer = (timer % 60).toLocaleString(undefined, {
      minimumIntegerDigits: 2,
    });
    setSeconds(secondFromTimer);

    setTimeout(() => {
      if (timer !== 0) {
        setTimer(timer - 1);
      } else {
        setToggleOtpTimer(false);
      }
    }, 1000);
  }, [timer]);

  const handleVerifyOtp = async (e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    setVerifyInProgress(true);
    setOtpErrorMessage(false);
    setOtpSendSuccessMessage(false);
    try {
      const res = await axios.post(
        "/auth/users/login",
        {
          mobile: phone,
        },
        {
          headers: {
            "x-otp-token": `Bearer ${otp}`,
          },
        }
      );
      Cookies.set("x-admin-auth", res.data.access_token, {
        expires: 7,
        secure: true,
        sameSite: "Strict",
      });
      Cookies.set("x-z-user", encrypted(res.data.profile), {
        expires: 7,
        secure: true,
        sameSite: "Strict",
      });
      window.location.replace("/");
    } catch (error) {
      const err = error as AxiosError;
      setVerifyInProgress(false);
      setOtpErrorMessage(true);
      setOtpSendSuccessMessage(false);
    }
  };

  const verifyOtp = async (e: React.BaseSyntheticEvent) => {
    handleVerifyOtp(e);
  };

  const resendOtp = async () => {
    handleTimerReset();
    handleGenerateOtp();
    setIsInvalidPin(false);
  };

  const handleTimerReset = () => {
    setTimer(timerValue);
    setToggleOtpTimer(true);
  };

  const handleGenerateOtp = async () => {
    try {
      const resOtp = await axios.post("/otp/generate", { phone: phone });
      if (resOtp.status === 200) {
        setOtpSendSuccessMessage(true);
        return resOtp;
      } else {
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.log("error", error);
      }
    }
  };

  return (
    <Box>
      <Box>
        <IconButton
          aria-label="back"
          icon={<FaArrowLeft />}
          onClick={handleBackButton}
          sx={{
            border: "1px solid",
            borderColor: theme.colors.primary.main,
            background: "transparent",
            color: theme.colors.primary.main,
            _hover: {
              background: theme.colors.primary.main,
              color: theme.colors.bg.main,
            },
          }}
        />
      </Box>
      <Box sx={{ maxWidth: "22rem" }}>
        <form onSubmit={verifyOtp}>
          <Box sx={{ marginTop: "2.5rem" }}>
            <Heading sx={{ marginBottom: theme.space.sm }}>
              Verifying it’s you
            </Heading>
            <Text
              sx={{
                fontSize: theme.fontSizes.subHeading2,
                marginBottom: theme.space["3xl"],
              }}
            >
              Enter the OTP sent to your phone
            </Text>
            <Box sx={{ marginBottom: "2rem" }}>
              <PinInput
                size={"lg"}
                placeholder="-"
                focusBorderColor={theme.colors.primary.main}
                onChange={(e) => handleInputChange(e)}
              >
                {new Array(pinLength).fill(0).map((_, i) => (
                  <PinInputField
                    key={i}
                    borderColor={theme.colors.primary.main}
                    _focus={{ backgroundColor: theme.colors.bg.main }}
                    autoComplete="off"
                    m="0.25rem"
                  />
                ))}
              </PinInput>
            </Box>
            <Flex
              sx={{ marginBottom: "1rem", justifyContent: "space-between" }}
            >
              {toggleOtpTimer === false && (
                <Box>
                  <Text
                    sx={{
                      fontSize: theme.fontSizes.body,
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                    color={theme.colors.primary.main}
                    onClick={resendOtp}
                  >
                    Resend OTP
                  </Text>
                </Box>
              )}
              {toggleOtpTimer === true && (
                <Box>
                  <Text
                    sx={{ fontSize: theme.fontSizes.body, display: "inline" }}
                    color={theme.colors.secondary.light}
                  >
                    Resend OTP
                  </Text>
                  <Text
                    sx={{ fontSize: theme.fontSizes.body, display: "inline" }}
                    color={theme.colors.secondary.main}
                  >
                    &nbsp; in {minutes}:{seconds} min
                  </Text>
                </Box>
              )}
            </Flex>

            <Box sx={{ minHeight: "3.25rem" }}>
              {otpSendSuccessMessage && (
                <Alert status="success">
                  <AlertIcon />
                  OTP sent to your phone!
                </Alert>
              )}
              {isInvalidPin && (
                <Alert status="error" borderRadius={"4px"}>
                  <AlertIcon />
                  Invalid OTP entered!
                </Alert>
              )}
            </Box>

            <Flex sx={{ flexDirection: "column", marginTop: "1rem" }}>
              <Box sx={{ width: "100%", marginTop: "1rem" }}>
                <Button
                  type="submit"
                  width="100%"
                  height="3rem"
                  rightIcon={<FaArrowRight />}
                  isLoading={verifyInProgress}
                  style={{
                    background: theme.colors.primary.main,
                    color: theme.colors.bg.main,
                  }}
                >
                  Submit
                </Button>
              </Box>
            </Flex>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default Otp;
